import { useCallback, useEffect, useRef, useState } from 'react'
import { brParaIso, isoParaBr, validarNascimentoBr } from '@precad/compartilhado/validacao/data'
import { chamar, enviarArquivos, exigirSucesso, type ArquivoParaEnviar } from './api'
import { ULTIMA_DISPONIVEL } from './etapas'
import type { DocumentoEnviado, Pendencia, Socio } from './tipos'

/**
 * Estado do preenchimento: carregamento, gravação, retomada e envio.
 *
 * Regras vindas do contrato da action, cada uma com um motivo:
 *
 *  1. `versaoRascunho` é OBRIGATÓRIA em `salvarEtapa`. É o que impede o celular
 *     sobrescrever calado o que foi digitado no computador.
 *  2. Campo AUSENTE mantém o valor; `""` APAGA. Por isso mandamos só os campos
 *     da etapa atual — salvar a etapa 3 não pode zerar a 2.
 *  3. A capability e a sessão vivem em cookie HttpOnly. O JavaScript nunca as
 *     vê, então "existe rascunho?" e "já verifiquei?" se descobrem chamando
 *     `estado`, não olhando storage.
 *
 * `verificado`, `validarCodigo` e `reenviarCodigo` estão DORMENTES: o wizard de
 * primeiro cadastro corre sem verificação de e-mail desde 15/08/2026. Não são
 * código morto — são o mecanismo da atualização cadastral, onde provar posse do
 * e-mail é o controle central. O backend continua aceitando sessão; só não a
 * exige. Religar o gate é voltar a checar `verificado` no App.
 */

export type Gravacao = 'nunca' | 'gravando' | 'salvo' | 'erro'

/** Desfecho do `iniciar` quando o CNPJ já está vivo na esteira. */
export interface CnpjExistente {
  situacao: 'emAnalise' | 'cadastrado'
  /** Data da solicitação, `YYYY-MM-DD`. Nunca o número — ele é sequencial. */
  desde: string | null
  mensagem: string
}

interface LinhaEstado {
  success?: number
  status?: string
  numero?: number | null
  etapaAtual?: number
  versaoRascunho?: number
  atualizadoEm?: string
  sessaoAtiva?: number
  socios?: Socio[]
  documentos?: DocumentoEnviado[]
  aceites?: Array<{ chave: string; versao: number }>
  pendencias?: Pendencia[]
  [campo: string]: unknown
}

/** Campos do cabeçalho que o formulário edita. O resto da resposta é estrutura. */
const NAO_SAO_CAMPOS = new Set([
  'success', 'status', 'numero', 'etapaAtual', 'versaoRascunho', 'atualizadoEm',
  'sessaoAtiva', 'socios', 'documentos', 'aceites', 'pendencias', 'ciclo',
])

/**
 * Nome do parâmetro que carrega a capability no link do e-mail de correção.
 *
 * Curto de propósito: clientes de e-mail quebram linha em URL longa, e cada
 * caractere a menos é uma chance a menos de o link chegar truncado.
 */
const PARAM_RETOMADA = 'r'

const ESPERA_AUTOSAVE = 2500

export function useRascunho() {
  const [carregando, setCarregando] = useState(true)
  const [existe, setExiste] = useState(false)
  const [verificado, setVerificado] = useState(false)
  const [dados, setDados] = useState<Record<string, string>>({})
  const [socios, setSocios] = useState<Socio[]>([])
  const [documentos, setDocumentos] = useState<DocumentoEnviado[]>([])
  const [aceites, setAceites] = useState<string[]>([])
  const [etapa, setEtapa] = useState(1)
  const [alcancada, setAlcancada] = useState(1)
  const [gravacao, setGravacao] = useState<Gravacao>('nunca')
  const [atualizadoEm, setAtualizadoEm] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [numero, setNumero] = useState<number | null>(null)
  const [prazoAnalise, setPrazoAnalise] = useState<string | null>(null)
  const [concluido, setConcluido] = useState(false)
  const [cnpjExistente, setCnpjExistente] = useState<CnpjExistente | null>(null)
  const [pendencias, setPendencias] = useState<Pendencia[]>([])

  const versao = useRef(0)
  const gravando = useRef(false)
  const sujos = useRef<Set<string>>(new Set())
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null)

  const absorver = useCallback((linha: LinhaEstado) => {
    if (typeof linha.versaoRascunho === 'number') versao.current = linha.versaoRascunho
    if (typeof linha.etapaAtual === 'number') setAlcancada(linha.etapaAtual)
    if (typeof linha.atualizadoEm === 'string') setAtualizadoEm(linha.atualizadoEm)
    /* O servidor fala ISO (`AAAA-MM-DD`); a tela fala `DD/MM/AAAA`. A conversão
       vive aqui e no `salvarSocios` — as duas únicas fronteiras — para que
       nenhuma tela precise saber do formato do fio. */
    if (Array.isArray(linha.socios)) {
      setSocios(
        linha.socios.map((s) => ({ ...s, dataNascimento: isoParaBr(s.dataNascimento) })),
      )
    }
    if (Array.isArray(linha.documentos)) setDocumentos(linha.documentos)
    if (Array.isArray(linha.aceites)) setAceites(linha.aceites.map((a) => a.chave))
    /* Sempre que vier. Uma resposta que traz o array vazio está dizendo "não há
       mais pendência" — é assim que o aviso some depois do reenvio, sem a tela
       precisar adivinhar. */
    if (Array.isArray(linha.pendencias)) setPendencias(linha.pendencias)
    if (linha.sessaoAtiva === 1) setVerificado(true)
  }, [])

  /* --- retomada ---------------------------------------------------------- */
  useEffect(() => {
    let vivo = true

    /**
     * Troca o `?r=` do link do e-mail pelo cookie de capability.
     *
     * Roda ANTES do `estado` porque é ele que cria o cookie que o `estado`
     * exige. Sem link na URL não custa nada — nem uma chamada.
     *
     * O parâmetro é removido da barra de endereço logo depois, e isso não é
     * cosmético: ali ele entra no histórico do navegador, é copiado junto
     * quando a pessoa compartilha o endereço, e sobrevive a um print de tela.
     * A chave já está no cookie; deixá-la à vista só cria caminhos para ela
     * vazar. Como o cookie sobrevive ao recarregamento, a pessoa também não
     * perde nada apertando F5.
     */
    async function adotarDoLink() {
      const url = new URL(window.location.href)
      const protocolo = url.searchParams.get(PARAM_RETOMADA)
      if (!protocolo) return

      url.searchParams.delete(PARAM_RETOMADA)
      window.history.replaceState(null, '', url.pathname + url.search + url.hash)

      /* Falha aqui é silenciosa de propósito: o `estado` logo abaixo tenta o
         cookie que já existir. Um link vencido cai na tela normal de começar
         do zero, em vez de numa mensagem de erro sobre um link que a pessoa
         nem sabe que clicou. */
      await chamar('adotar', { dados: { protocolo } }).catch(() => undefined)
    }

    adotarDoLink()
      .then(() => chamar<LinhaEstado>('estado'))
      .then((linhas) => {
        if (!vivo) return
        const linha = linhas[0]
        if (!linha || linha.success === 0) {
          /* Sem rascunho é o caso NORMAL de quem chega pela primeira vez. */
          setCarregando(false)
          return
        }

        const texto: Record<string, string> = {}
        for (const [chave, valor] of Object.entries(linha)) {
          if (NAO_SAO_CAMPOS.has(chave)) continue
          texto[chave] = valor == null ? '' : String(valor)
        }
        absorver(linha)
        setDados(texto)
        setExiste(true)

        if (linha.status && linha.status !== 'Rascunho' && linha.status !== 'Aguardando Correção') {
          /* Já enviado: a tela é a de conclusão, não o wizard. */
          setNumero(linha.numero ?? null)
          setConcluido(true)
        } else {
          setEtapa(Math.min(Math.max(2, linha.etapaAtual ?? 2), ULTIMA_DISPONIVEL))
        }
        setGravacao('salvo')
        setCarregando(false)
      })
      .catch(() => {
        /* Falha de rede na abertura não vira tela de erro: a pessoa ainda não
           fez nada e o caminho de começar do zero continua aberto. */
        if (vivo) setCarregando(false)
      })

    return () => {
      vivo = false
    }
  }, [absorver])

  /* --- gravação de campo ------------------------------------------------- */
  const gravar = useCallback(
    async (numeroEtapa: number, campos: string[]): Promise<boolean> => {
      if (gravando.current) return false
      gravando.current = true
      setGravacao('gravando')
      setErro(null)

      const corpo: Record<string, string | number> = {
        etapa: numeroEtapa,
        versaoRascunho: versao.current,
      }
      for (const campo of campos) corpo[campo] = dados[campo] ?? ''

      try {
        const linha = exigirSucesso(await chamar<LinhaEstado>('salvarEtapa', { dados: corpo }))
        absorver(linha)
        for (const campo of campos) sujos.current.delete(campo)
        setGravacao('salvo')
        return true
      } catch (e) {
        const falha = e as { message: string; codigoPrecad?: number | null }
        setGravacao('erro')
        setErro(
          falha.codigoPrecad === 50521
            ? 'Este preenchimento foi alterado em outro dispositivo. Recarregue a página para continuar de onde parou.'
            : falha.message,
        )
        return false
      } finally {
        gravando.current = false
      }
    },
    [dados, absorver],
  )

  /* --- autosave ---------------------------------------------------------- */
  useEffect(() => {
    if (!existe || concluido || etapa > 3 || sujos.current.size === 0) return
    if (relogio.current) clearTimeout(relogio.current)

    relogio.current = setTimeout(() => {
      const campos = [...sujos.current]
      if (campos.length && !gravando.current) void gravar(Math.min(etapa, 3), campos)
    }, ESPERA_AUTOSAVE)

    return () => {
      if (relogio.current) clearTimeout(relogio.current)
    }
  }, [dados, existe, etapa, concluido, gravar])

  /* --- ações ------------------------------------------------------------- */
  const alterar = useCallback((campo: string, valor: string) => {
    sujos.current.add(campo)
    setDados((atual) => ({ ...atual, [campo]: valor }))
  }, [])

  const definirVarios = useCallback((valores: Record<string, string>) => {
    for (const campo of Object.keys(valores)) sujos.current.add(campo)
    setDados((atual) => ({ ...atual, ...valores }))
  }, [])

  const iniciar = useCallback(
    async (cnpj: string, email: string, turnstileToken: string): Promise<boolean> => {
      setGravacao('gravando')
      setErro(null)
      setCnpjExistente(null)
      try {
        const linhas = await chamar<
          LinhaEstado & { errorCode?: string; message?: string; situacao?: string; desde?: string }
        >('iniciar', { dados: { cnpj, email }, turnstileToken })

        /* CNPJ já na esteira não é erro de preenchimento — é um desfecho, com
           tela própria. Por isso é interceptado ANTES do `exigirSucesso`: ele
           lança um Error e descartaria `situacao` e `desde`, que são o conteúdo
           da tela. */
        const bruta = linhas[0]
        if (bruta?.success === 0 && bruta.errorCode === 'PRECAD-50512') {
          setCnpjExistente({
            situacao: bruta.situacao === 'cadastrado' ? 'cadastrado' : 'emAnalise',
            desde: bruta.desde ?? null,
            mensagem: bruta.message ?? '',
          })
          setGravacao('nunca')
          return false
        }

        const linha = exigirSucesso(linhas)
        absorver(linha)
        setDados((atual) => ({ ...atual, cnpj, email }))
        setExiste(true)
        setEtapa(2)
        setGravacao('salvo')
        return true
      } catch (e) {
        setGravacao('erro')
        setErro((e as Error).message)
        return false
      }
    },
    [absorver],
  )

  const validarCodigo = useCallback(async (codigo: string): Promise<boolean> => {
    setGravacao('gravando')
    setErro(null)
    try {
      const linha = exigirSucesso(
        await chamar<LinhaEstado & { avisoDuplicidade?: number }>('validarCodigo', { dados: { codigo } }),
      )
      setVerificado(true)
      /* Só agora, com o e-mail comprovado, a duplicidade pode ser revelada — e
         chega antes dos uploads, que é o ponto de avisar. */
      if (linha.avisoDuplicidade === 1) {
        setErro(
          'Atenção: este CNPJ já possui uma solicitação em andamento. Você pode continuar, mas ela será analisada como duplicada.',
        )
      }
      setGravacao('salvo')
      return true
    } catch (e) {
      setGravacao('salvo')
      setErro((e as Error).message)
      return false
    }
  }, [])

  const reenviarCodigo = useCallback(async (): Promise<string | null> => {
    try {
      await chamar('solicitarCodigo', {})
      return null
    } catch (e) {
      return (e as Error).message
    }
  }, [])

  const salvarSocios = useCallback(async (lista: Socio[]): Promise<boolean> => {
    setGravacao('gravando')
    setErro(null)
    try {
      const linha = exigirSucesso(
        await chamar<LinhaEstado>('salvarSocios', {
          dados: {
            socios: lista.map((s) => ({
              nome: s.nome,
              cpf: s.cpf,
              email: s.email ?? null,
              /* `brParaIso` devolve '' para data inválida, e o `|| null` a
                 transforma em nulo. A tela já barrou esse caso antes de chegar
                 aqui — isto é a rede de baixo, não a validação.

                 A checagem de nascimento vem explícita porque `brParaIso` só
                 exige data REAL: futuro converte (é o que a vigência de crédito
                 precisa), e sem esta linha um nascimento em 2099 escorregaria
                 para o banco. */
              dataNascimento: validarNascimentoBr(s.dataNascimento ?? '')
                ? brParaIso(s.dataNascimento ?? '')
                : null,
            })),
          },
        }),
      )
      absorver(linha)
      setGravacao('salvo')
      return true
    } catch (e) {
      setGravacao('erro')
      setErro((e as Error).message)
      return false
    }
  }, [absorver])

  const anexar = useCallback(
    async (arquivos: ArquivoParaEnviar[], aoProgredir: (f: number) => void): Promise<boolean> => {
      setGravacao('gravando')
      setErro(null)
      try {
        const linha = exigirSucesso(await enviarArquivos<LinhaEstado>('anexar', arquivos, aoProgredir))
        absorver(linha)
        setGravacao('salvo')
        return true
      } catch (e) {
        setGravacao('erro')
        setErro((e as Error).message)
        return false
      }
    },
    [absorver],
  )

  const salvarAceites = useCallback(async (chaves: string[]): Promise<boolean> => {
    setGravacao('gravando')
    setErro(null)
    try {
      const linha = exigirSucesso(await chamar<LinhaEstado>('aceitar', { dados: { chaves } }))
      absorver(linha)
      setGravacao('salvo')
      return true
    } catch (e) {
      setGravacao('erro')
      setErro((e as Error).message)
      return false
    }
  }, [absorver])

  const enviar = useCallback(async (): Promise<boolean> => {
    setGravacao('gravando')
    setErro(null)
    try {
      const linha = exigirSucesso(
        await chamar<{ success?: number; numero?: number; prazoAnalise?: string | null }>('enviar', {}),
      )
      setNumero(linha.numero ?? null)
      setPrazoAnalise(linha.prazoAnalise ?? null)
      setConcluido(true)
      setGravacao('salvo')
      return true
    } catch (e) {
      setGravacao('erro')
      setErro((e as Error).message)
      return false
    }
  }, [])

  /**
   * Solta este navegador do preenchimento atual e volta à estaca zero.
   *
   * Existe por causa de um beco real: quem já enviou volta ao link público e
   * cai eternamente na tela do número, porque o cookie continua apontando para
   * a solicitação. Ela é útil no minuto seguinte ao envio e um obstáculo uma
   * semana depois, quando a pessoa quer ver o andamento, assinar a ficha ou
   * abrir cadastro para outro CNPJ.
   *
   * A limpeza local vem ANTES da rede, e a falha da rede é engolida de
   * propósito: destravar a tela é o que a pessoa pediu ao clicar, e isso não
   * pode depender de a chamada dar certo. Se ela falhar, o cookie sobrevive e
   * um F5 traz o beco de volta — pior do que o ideal, melhor do que ficar preso
   * com uma mensagem de erro.
   *
   * NADA é invalidado no banco. O rascunho continua lá e o link `?r=` do e-mail
   * de correção continua funcionando — é o que torna o botão seguro de apertar.
   */
  const sair = useCallback(async (): Promise<void> => {
    if (relogio.current) clearTimeout(relogio.current)
    sujos.current.clear()
    versao.current = 0

    setExiste(false)
    setConcluido(false)
    setVerificado(false)
    setCnpjExistente(null)
    setDados({})
    setSocios([])
    setDocumentos([])
    setAceites([])
    setPendencias([])
    setNumero(null)
    setPrazoAnalise(null)
    setAtualizadoEm(null)
    setEtapa(1)
    setAlcancada(1)
    setGravacao('nunca')
    setErro(null)

    await chamar('sair').catch(() => undefined)
  }, [])

  const irPara = useCallback((n: number) => {
    setEtapa(Math.min(Math.max(1, n), ULTIMA_DISPONIVEL))
    setErro(null)
  }, [])

  return {
    carregando, existe, verificado, concluido, cnpjExistente,
    dados, socios, documentos, aceites, pendencias,
    etapa, alcancada, gravacao, atualizadoEm, erro, numero, prazoAnalise,
    alterar, definirVarios, setSocios, setAceites,
    iniciar, salvar: gravar, validarCodigo, reenviarCodigo,
    salvarSocios, anexar, salvarAceites, enviar, sair,
    irPara, limparErro: () => setErro(null),
    recomecar: () => setCnpjExistente(null),
  }
}
